import * as echarts from 'echarts';

/**
 * Transforms API trend_list data into chart format
 * @param trendList - Array of trend data from API response
 * @param metricName - Name of the metric to extract
 * @param dateDimension - 'DAY' | 'WEEK' | 'MONTH'
 * @param formatMessage - Intl formatMessage function
 * @returns Object with xAxisData and series for chart
 */
export const transformTrendData = (
  trendList: Array<{
    date: string; // Format: "20251110"
    metrics: Record<string, {
      value: string;
      last_value?: string;
    }>;
  }>,
  metricName: string,
  dateDimension: string = 'DAY',
  formatMessage?: any
) => {
  if (!trendList || trendList.length === 0) {
    return {
      xAxisData: [],
      series: [],
    };
  }

  // Extract xAxisData (dates) - convert from "20251110" to appropriate format
  const xAxisData = trendList.map((item) => {
    const dateStr = item.date;
    if (dateStr.length === 8) {
      const year = dateStr.slice(0, 4);
      const month = dateStr.slice(4, 6);
      const day = dateStr.slice(6, 8);
      
      // Format based on dateDimension:
      // - For MONTH: use "yyyy-mm" (e.g., "2025-01")
      // - For DAY/WEEK: use "yyyy-mm-dd" (e.g., "2025-01-15")
      if (dateDimension === 'MONTH') {
        return `${year}-${month}`;
      } else {
        return `${year}-${month}-${day}`;
      }
    }
    return dateStr;
  });

  // Extract values for current period (value) and last period (last_value)
  const currentValues: (number | null)[] = [];
  const lastValues: (number | null)[] = [];

  trendList.forEach((item) => {
    const metric = item.metrics?.[metricName];
    if (metric) {
      // Parse value to number, keep as null if empty string or invalid
      const currentValStr = metric.value;
      if (currentValStr === '' || currentValStr === null || currentValStr === undefined) {
        currentValues.push(null);
      } else {
        const currentVal = parseFloat(currentValStr);
        currentValues.push(isNaN(currentVal) ? null : currentVal);
      }

      // Parse last_value, keep as null if empty string or not available
      const lastValStr = metric.last_value;
      if (lastValStr === '' || lastValStr === null || lastValStr === undefined) {
        lastValues.push(null);
      } else {
        const lastVal = parseFloat(lastValStr);
        lastValues.push(isNaN(lastVal) ? null : lastVal);
      }
    } else {
      currentValues.push(null);
      lastValues.push(null);
    }
  });

  // Format series similar to dashboard formatSeries
  const seriesNameFirst = dateDimension === 'DAY' ? 'card_today' : dateDimension === 'WEEK' ? 'card_week' : 'card_month';
  const seriesNameTwo = dateDimension === 'DAY' ? 'sdlw' : dateDimension === 'WEEK' ? 'swlm' : 'smly';

  const series = [
    {
      name: formatMessage ? formatMessage({ id: seriesNameFirst }) : 'Today',
      type: 'line',
      showSymbol: currentValues.length === 1 || currentValues.filter((e) => e !== null && e !== undefined).length === 1,
      symbol: 'circle',
      z: 2,
      lineStyle: {
        type: 'solid' as const,
        color: '#7F6FF1', // Purple color for the line
      },
      itemStyle: {
        color: '#7F6FF1', // Purple color for the symbol
      },
      areaStyle: {
        opacity: 0.25,
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {
            offset: 0,
            color: '#7F6FF1', // Purple color instead of blue
          },
          {
            offset: 1,
            color: '#FFF',
          },
        ]),
      },
      emphasis: {
        focus: 'series' as const,
      },
      data: currentValues,
    },
    {
      name: formatMessage ? formatMessage({ id: seriesNameTwo }) : 'SDLW',
      type: 'line',
      showSymbol: lastValues.length === 1 || lastValues.filter((e) => e !== null && e !== undefined).length === 1,
      symbol: 'circle',
      z: 1,
      lineStyle: {
        type: 'dotted' as const,
        color: '#5EC8F1', // Green color for the line
      },
      itemStyle: {
        color: '#5EC8F1', // Green color for the symbol
      },
      areaStyle: {
        opacity: 0.25,
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {
            offset: 0,
            color: '#5EC8F1',
          },
          {
            offset: 1,
            color: '#FFF',
          },
        ]),
      },
      emphasis: {
        focus: 'series' as const,
      },
      data: lastValues,
    },
  ];

  return {
    xAxisData,
    series,
  };
};

